import React from 'react';
import Panel from '../../components/ui/Panel';
import CodeBlock from '../../components/ui/CodeBlock';

const ShadowsPage: React.FC = () => {
  const tailwindConfigCode = `// tailwind.config.js
module.exports = {
  theme: {
    extend: {
      boxShadow: {
        'osrs-sm': '0 1px 2px 0 rgba(0, 0, 0, 0.5)',
        'osrs-md': '0 2px 4px 0 rgba(0, 0, 0, 0.5)',
        'osrs-lg': '0 4px 6px -1px rgba(0, 0, 0, 0.5)',
        'osrs-inner': 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.3)',
        'osrs-text': '1px 1px 1px rgba(0, 0, 0, 0.7)',
      },
    },
  },
}`;

  const cssCode = `/* CSS Text Shadow */
.osrs-text-shadow {
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.7);
}

/* Pixel-perfect shadow for small UI elements */
.osrs-pixel-shadow {
  box-shadow: 1px 1px 0 rgba(0, 0, 0, 0.8);
}`;

  return (
    <div>
      <h1 id="overview" className="text-3xl text-osrs-gold font-bold uppercase mb-4">Shadows</h1>
      <p className="text-osrs-yellow mb-8">
        While the original OSRS interface uses shadows sparingly, our design system incorporates subtle
        shadows to enhance usability while maintaining the authentic OSRS look and feel.
      </p>
      
      <section id="styles" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-4">Shadow Styles</h2>
        <p className="text-osrs-yellow mb-6">
          The OSRS Design System uses a few key shadow styles to create depth and emphasize UI elements.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          <div>
            <h3 className="text-xl text-osrs-gold font-bold uppercase mb-3">Box Shadows</h3>
            
            <div className="space-y-6">
              <div>
                <div className="bg-osrs-panel border-3 border-osrs-border p-4 mb-2">
                  <p className="text-osrs-yellow">No Shadow</p>
                </div>
                <p className="text-osrs-yellow text-sm">Default for most elements (no shadow)</p>
              </div>
              
              <div>
                <div className="bg-osrs-panel border-3 border-osrs-border p-4 mb-2 shadow-osrs-sm">
                  <p className="text-osrs-yellow">Small Shadow</p>
                </div>
                <p className="text-osrs-yellow text-sm">Subtle shadow for slight elevation (shadow-osrs-sm)</p>
              </div>
              
              <div>
                <div className="bg-osrs-panel border-3 border-osrs-border p-4 mb-2 shadow-osrs-md">
                  <p className="text-osrs-yellow">Medium Shadow</p>
                </div>
                <p className="text-osrs-yellow text-sm">Medium shadow for interactive elements (shadow-osrs-md)</p>
              </div>
              
              <div>
                <div className="bg-osrs-panel border-3 border-osrs-border p-4 mb-2 shadow-osrs-lg">
                  <p className="text-osrs-yellow">Large Shadow</p>
                </div>
                <p className="text-osrs-yellow text-sm">Large shadow for floating elements (shadow-osrs-lg)</p>
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="text-xl text-osrs-gold font-bold uppercase mb-3">Special Shadows</h3>
            
            <div className="space-y-6">
              <div>
                <div className="bg-osrs-panel border-3 border-osrs-border p-4 mb-2 shadow-osrs-inner">
                  <p className="text-osrs-yellow">Inner Shadow</p>
                </div>
                <p className="text-osrs-yellow text-sm">Inset shadow for recessed elements (shadow-osrs-inner)</p>
              </div>
              
              <div>
                <div className="bg-osrs-dark-brown border-3 border-osrs-border p-4 mb-2">
                  <p className="text-osrs-yellow shadow-osrs-text">Text Shadow</p>
                </div>
                <p className="text-osrs-yellow text-sm">Shadow for better text readability (shadow-osrs-text)</p>
              </div>
              
              <div>
                <div className="bg-osrs-panel border-3 border-osrs-border p-4 mb-2" style={{ boxShadow: '1px 1px 0 rgba(0, 0, 0, 0.8)' }}>
                  <p className="text-osrs-yellow">Pixel Shadow</p>
                </div>
                <p className="text-osrs-yellow text-sm">Pixel-perfect shadow (1px offset)</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section id="implementation" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-4">Implementation</h2>
        
        <Panel title="Tailwind Config" className="mb-6">
          <p className="text-osrs-yellow mb-4">
            Extend Tailwind's shadow configurations for OSRS-specific values:
          </p>
          <CodeBlock code={tailwindConfigCode} language="jsx" />
        </Panel>
        
        <Panel title="Custom CSS Shadows" className="mb-6">
          <p className="text-osrs-yellow mb-4">
            Additional CSS for specialized shadows:
          </p>
          <CodeBlock code={cssCode} language="css" />
        </Panel>
      </section>
      
      <section id="usage" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-4">Usage Guidelines</h2>
        
        <Panel className="mb-6">
          <h3 className="text-xl text-osrs-gold font-bold mb-3">When to Use Shadows</h3>
          <p className="text-osrs-yellow mb-2">
            Shadows should be used sparingly in OSRS interfaces:
          </p>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Use shadows primarily for interactive elements to indicate they can be clicked</li>
            <li>Apply text shadows to improve readability when text is on a complex background</li>
            <li>Use inner shadows for recessed elements like input fields</li>
            <li>Consider pixel-perfect shadows (1px offset) for small UI elements to maintain the retro look</li>
            <li>Avoid heavy shadows that would make the interface look modern rather than nostalgic</li>
          </ul>
        </Panel>
        
        <Panel className="mb-6">
          <h3 className="text-xl text-osrs-gold font-bold mb-3">Component-Specific Shadows</h3>
          <p className="text-osrs-yellow mb-2">
            Recommended shadow usage for different components:
          </p>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Buttons: Small shadow (osrs-sm) for default state, medium shadow (osrs-md) when hovered</li>
            <li>Panels: No shadow or very subtle shadow</li>
            <li>Tooltips and popovers: Medium shadow (osrs-md) to indicate floating above content</li>
            <li>Input fields: Inner shadow (osrs-inner) to appear recessed</li>
            <li>Modal dialogs: Large shadow (osrs-lg) to emphasize being above other content</li>
            <li>Inventory items: Pixel-perfect shadow for a retro feel</li>
          </ul>
        </Panel>
        
        <Panel className="mb-6">
          <h3 className="text-xl text-osrs-gold font-bold mb-3">Accessibility Considerations</h3>
          <p className="text-osrs-yellow mb-2">
            When using shadows, consider the following:
          </p>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Text shadows should enhance readability, not hinder it</li>
            <li>Make sure shadow colors maintain sufficient contrast with background colors</li>
            <li>Don't rely solely on shadows to indicate interactive elements; use color and shape as well</li>
            <li>Test your interface in different lighting conditions to ensure shadows are effective</li>
          </ul>
        </Panel>
      </section>
    </div>
  );
};

export default ShadowsPage; 